import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import Layout from "../components/layout/GSTlayout";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ClientPerformancePage = () => {
  // State for filtering
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");

  // Sample client performance data (replace with actual API data)
  const clientPerformanceData = {
    topClients: [
      {
        name: "Xyz Corporation",
        revenue: 1.2,
        compliance: 95,
        status: "Excellent",
      },
      { name: "ABC Enterprises", revenue: 0.9, compliance: 88, status: "Good" },
      {
        name: "Tech Innovations Ltd",
        revenue: 0.7,
        compliance: 82,
        status: "Average",
      },
      {
        name: "Global Solutions Inc",
        revenue: 0.5,
        compliance: 75,
        status: "Needs Improvement",
      },
      {
        name: "Innovative Traders",
        revenue: 0.4,
        compliance: 70,
        status: "Poor",
      },
    ],
    revenueByClientType: {
      labels: ["Large Enterprises", "SMEs", "Startups", "Individual"],
      datasets: [
        {
          data: [45, 30, 15, 10],
          backgroundColor: [
            "rgba(255, 99, 132, 0.6)",
            "rgba(54, 162, 235, 0.6)",
            "rgba(255, 206, 86, 0.6)",
            "rgba(75, 192, 192, 0.6)",
          ],
        },
      ],
    },
    clientComplianceDistribution: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      datasets: [
        {
          label: "Compliance Percentage",
          data: [85, 88, 90, 86, 92, 89],
          backgroundColor: "rgba(75, 192, 192, 0.6)",
        },
      ],
    },
  };

  // Chart options
  const barOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Client Compliance Trend" },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => value + "%",
        },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { position: "right" },
      title: { display: true, text: "Revenue by Client Type" },
    },
  };

  // Determine status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case "Excellent":
        return "success";
      case "Good":
        return "primary";
      case "Average":
        return "warning";
      case "Needs Improvement":
        return "error";
      case "Poor":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Client Performance Dashboard
        </Typography>

        {/* Filtering Controls */}
        <Box sx={{ display: "flex", mb: 3, gap: 2 }}>
          <FormControl variant="outlined" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              label="Year"
            >
              {[2020, 2021, 2022, 2023, 2024].map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" sx={{ minWidth: 120 }}>
            <InputLabel>Quarter</InputLabel>
            <Select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              label="Quarter"
            >
              {["Q1", "Q2", "Q3", "Q4"].map((quarter) => (
                <MenuItem key={quarter} value={quarter}>
                  {quarter}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={3}>
          {/* Client Compliance Trend Chart */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 2, height: "100%" }}>
              <Bar
                data={clientPerformanceData.clientComplianceDistribution}
                options={barOptions}
              />
            </Paper>
          </Grid>

          {/* Revenue by Client Type */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, height: "100%" }}>
              <Pie
                data={clientPerformanceData.revenueByClientType}
                options={pieOptions}
              />
            </Paper>
          </Grid>

          {/* Top Clients Table */}
          <Grid item xs={12}>
            <Paper elevation={3}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client Name</TableCell>
                      <TableCell align="right">Revenue (Cr)</TableCell>
                      <TableCell align="right">Compliance %</TableCell>
                      <TableCell align="right">Performance Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientPerformanceData.topClients.map((client) => (
                      <TableRow key={client.name}>
                        <TableCell component="th" scope="row">
                          {client.name}
                        </TableCell>
                        <TableCell align="right">{client.revenue}</TableCell>
                        <TableCell align="right">
                          {client.compliance}%
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={client.status}
                            color={getStatusColor(client.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default ClientPerformancePage;
